// app/src/features/reforecast/ViewMomentBlock.jsx
import React from "react";
import { C } from "../../lib/colors.js";
import { scrubPosition } from "../../lib/forecast/reforecastEntries.js";
import { fmtDateTime } from "../../lib/formatDates.js";
import { ui } from "../../lib/telemetry/instrument.js";
import { AXIS_H } from "../../lib/timeline/geometry.js";

/**
 * The frame's `viewMomentBlock` slot: the past-state pill over the date axis while a past
 * forecast state is viewed — its prev / next steps, the "Showing the forecast from …" label with
 * the position counter and the return to latest. A desktop slot: `AppFrame` mounts `PhoneApp`
 * alone on a phone and never renders it there.
 */
export default function ViewMomentBlock({ ctx }) {
  const {
    viewMomentIso,
    fcMoments,
    scrubToMoment,
    returnToLive,
    scrubLoading,
    viewSnap,
    scrubIsLive,
    tsdH,
    signpostsH,
  } = ctx;
  return (
    <>
      {/* fcMoments' last entry is the at-rest/latest position (the newest resolution when
       resolved, else the live forecast sentinel); clicking the label returns to live. The
       chrome mirrors the report modal's prev/next (same polygon glyph). */}
      {viewMomentIso &&
        (() => {
          // Shared position model (same helper the report-modal carousel
          // uses) so the pill's "i of n" and the modal's can never drift.
          const { pos, total, prevMoment, nextMoment } = scrubPosition(viewMomentIso, fcMoments);
          const canPrev = prevMoment != null;
          const canNext = nextMoment != null;
          const goPrev = () => {
            if (canPrev) scrubToMoment(prevMoment);
          };
          const goNext = () => {
            // scrubToMoment maps the TAIL (the last entry) back to latest, so
            // stepping onto it returns to live; the live forecast sentinel (now
            // before any resolution tail) stays a real, selectable stop.
            if (canNext) scrubToMoment(nextMoment);
          };
          const Arrow = ({ dir }) => (
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="currentColor"
              style={{
                transform: `rotate(${dir === "left" ? 180 : 0}deg)`,
                display: "block",
              }}
            >
              <polygon points="6,4 6,20 20,12" />
            </svg>
          );
          const onKey = (e, fn) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              fn();
            }
          };
          // Only show the counter when viewMomentIso is actually PRESENT in
          // fcMoments. Guarding on pos/total alone isn't enough: scrubPosition
          // maps an unknown/stale moment (e.g. fcMoments changed mid-scrub) to
          // the live tail (pos = total-1), which would misread as "N of N".
          // Empty list → total 0. Either way, omit rather than mislead.
          const known = fcMoments.indexOf(viewMomentIso) >= 0;
          const counter = known && total > 0 ? ` · ${pos + 1} of ${total}` : "";
          return (
            <div
              style={{
                position: "absolute",
                left: "50%",
                // Anchored from the bottom: the axis bar is AXIS_H tall and sits above the TSD
                // drawer (height tsdH), so the pill tracks the bar whether the drawer is open
                // or closed.
                bottom: `calc(${AXIS_H}px + 14px + ${tsdH}px + ${signpostsH}px)`,
                transform: "translateX(-50%)",
                zIndex: 30,
                display: "flex",
                alignItems: "center",
                gap: 5,
                background: C.action,
                color: C.btnText,
                fontSize: 11,
                fontFamily: '"IBM Plex Mono",monospace',
                fontWeight: "bold",
                letterSpacing: 0,
                padding: "3px 8px",
                borderRadius: 12,
                whiteSpace: "nowrap",
                boxShadow: `0 2px 10px ${C.shadow}`,
                userSelect: "none",
              }}
            >
              <span
                {...ui("timeline.scrub-prev")}
                role="button"
                aria-label="Previous forecast state"
                aria-disabled={!canPrev}
                tabIndex={canPrev ? 0 : -1}
                onKeyDown={(e) => onKey(e, goPrev)}
                onClick={goPrev}
                title="Previous forecast state"
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0 2px",
                  cursor: canPrev ? "pointer" : "default",
                  opacity: canPrev ? 1 : 0.3,
                }}
              >
                <Arrow dir="left" />
              </span>
              <span
                {...ui("timeline.scrub-latest")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => onKey(e, () => returnToLive())}
                onClick={() => returnToLive()}
                title="Return to the latest forecast"
                // FIXED width (in `ch`, since the pill is monospace) so the
                // center-anchored pill's total width never changes and the flanking
                // ‹ › arrows stay stationary as the label shifts between
                // vintages/states: the verb, the per-vintage timestamp, the counter
                // digits and the "· loading…" suffix would otherwise all resize the
                // label. 86ch fits the longest state ("Couldn't load <longest date>
                // · N of M · loading… · Click to return to latest" plus the ⟲).
                // `maxWidth` caps it on narrow viewports so the pill can't hang
                // off-screen, and `textOverflow: ellipsis` truncates gracefully if
                // it ever clips.
                style={{
                  cursor: "pointer",
                  width: "86ch",
                  maxWidth: "88vw",
                  flexShrink: 0,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {scrubLoading
                  ? `Loading the forecast from ${fmtDateTime(viewMomentIso)}${counter}…`
                  : // The live forecast sentinel carries no rewound snapshot (viewSnap null),
                    // so treat it as a valid "viewing latest" position (scrubIsLive) rather
                    // than a load miss; otherwise scrubbing to it reads as "Couldn't load".
                    viewSnap || scrubIsLive
                    ? `Showing the forecast from ${fmtDateTime(viewMomentIso)}${counter}`
                    : `Couldn't load the forecast from ${fmtDateTime(viewMomentIso)}${counter}`}{" "}
                · Back to latest
              </span>
              <span
                {...ui("timeline.scrub-next")}
                role="button"
                aria-label="Next forecast state"
                aria-disabled={!canNext}
                tabIndex={canNext ? 0 : -1}
                onKeyDown={(e) => onKey(e, goNext)}
                onClick={goNext}
                title="Next forecast state"
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0 2px",
                  cursor: canNext ? "pointer" : "default",
                  opacity: canNext ? 1 : 0.3,
                }}
              >
                <Arrow dir="right" />
              </span>
            </div>
          );
        })()}
    </>
  );
}
